import { useContext, useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { updateAvatar } from "../../../controllers/accounts/accounts"
import { deleteProfilePhoto, uploadProfilePhoto } from "../../../controllers/posts/posts"
import { Box, CircularProgress, Grid, IconButton, Paper, Typography } from "@mui/material"
import { Add, Delete, Edit, PhotoCamera } from "@mui/icons-material";
import { VisuallyHiddenInput } from "../../../views/account/edit"
import { UserContext } from "../../../context/UserContext"

const PRIDE_GRADIENT = 'linear-gradient(135deg,#E40303,#FF8C00,#FFED00,#008026,#24408E,#732982)'

// Filled gallery slot — edit/delete reveal on hover instead of sitting above
// the image permanently, matching the hover-camera treatment on the avatar.
const PhotoTile = ({ item, onEdit, onDelete }) => (
    <Box sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        aspectRatio: '1 / 1',
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        '&:hover .photo-tile-overlay': { opacity: 1 },
        '&:hover .photo-tile-img': { transform: 'scale(1.04)' },
    }}>
        <Box
            component="img"
            className="photo-tile-img"
            src={item.img}
            alt="Profile"
            onClick={onEdit}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.2s ease' }}
        />
        <Box className="photo-tile-overlay" sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0) 55%)',
            opacity: 0,
            transition: 'opacity 0.15s ease',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', p: 1, gap: 0.5,
        }}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit() }} sx={{ background: 'rgba(255,255,255,0.92)', '&:hover': { background: '#fff' } }}>
                <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete() }} sx={{ background: 'rgba(255,255,255,0.92)', color: '#EF4444', '&:hover': { background: '#fff' } }}>
                <Delete fontSize="small" />
            </IconButton>
        </Box>
    </Box>
)

// Empty gallery slot — dashed drop-zone look instead of a bare bordered
// button, so it reads as "add here" at a glance.
const UploadTile = ({ onSelect, warning }) => (
    <Box sx={{ height: '100%' }}>
        {warning}
        <Box
            component="label"
            sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: '100%', minHeight: 140,
                borderRadius: 2,
                border: '2px dashed #d8dbe3',
                background: '#fafbfc',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: '#732982', background: '#f5f0f9' },
            }}
        >
            <Add sx={{ fontSize: 28, color: '#8A95A3', mb: 0.5 }} />
            <Typography variant="body2" sx={{ color: '#8A95A3', fontWeight: 600 }}>Add Photo</Typography>
            <VisuallyHiddenInput type="file" accept="image/*" onChange={(value) => onSelect(value.target.files[0])} />
        </Box>
    </Box>
)

export const PhotoGrid = ({ photo_list_value, set_photo_list_value, token, avatar, set_avatar, sendMongoDBRegistration }) => {
    const { userDetails, setUserDetails } = useContext(UserContext)
    const [globalReplaceImg, setGlobalReplaceImg] = useState(null)
    const [loadingContainer, setLoadingContainer] = useState(false)
    const validPhotoCount = photo_list_value.filter((item) => item?.img).length
    const totalImageCount = (avatar ? 1 : 0) + validPhotoCount
    const minimumAdditionalPhotos = avatar ? 2 : 3

    const queryClient = useQueryClient()

    // Upload a profile gallery photo — this wraps it in its own public post so
    // Mastodon doesn't prune it as unattached media (see posts.js).
    const mutation_upload_photo = useMutation({
        mutationFn: uploadProfilePhoto,
        onSuccess: (data) => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['getSingleAccount', userDetails?.id] })
        if(globalReplaceImg){
            const index = photo_list_value.indexOf(globalReplaceImg);

            // Replace the element at the found index
            if (index !== -1) {
                photo_list_value[index] = {id:data.id, img:data.img, status_id:data.status_id};
            }

            set_photo_list_value(photo_list_value => [...photo_list_value]);
            setLoadingContainer(false)
            sendMongoDBRegistration([...photo_list_value])

        } else {
            set_photo_list_value(imageUrls => [...imageUrls, {id:data.id, img:data.img, status_id:data.status_id}])
            setLoadingContainer(false)
            sendMongoDBRegistration([...photo_list_value, {id:data.id, img:data.img, status_id:data.status_id}])
        }
        },
    })


    const handleUploadPhoto = (image) => {
        setLoadingContainer(true)
        mutation_upload_photo.mutate({
            image,
            token
        })
    }

    // Delete a profile gallery photo — removes the wrapping post (legacy photos with
    // no status_id fall back to deleting the bare Mastodon media attachment).
    const mutation_delete_photo = useMutation({
        mutationFn: deleteProfilePhoto,
        onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['getSingleAccount', userDetails?.id] })
        setGlobalReplaceImg(null)

        sendMongoDBRegistration()
        },
    })

    const handleDeletePhoto = (item) => {
        set_photo_list_value(imageUrls => imageUrls.filter(i => i.id !== item.id))
        mutation_delete_photo.mutate({ status_id: item.status_id, media_id: item.id, token })
    }

    // update avatar mutation
    const mutation_avatar = useMutation({
        mutationFn: updateAvatar,
        onSuccess: (data) => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['getSingleAccount', userDetails?.id] })
            set_avatar(data.avatar)
            
            // Update UserContext and localStorage with new avatar
            const updatedUserDetails = { ...userDetails, avatar: data.avatar }
            setUserDetails(updatedUserDetails)
            localStorage.setItem('user', JSON.stringify(updatedUserDetails))
            
            setLoadingContainer(false)
        },
    })

    const updateAccountAvatar = (image) => {
        setLoadingContainer(true)
        mutation_avatar.mutate({
            image, 
            token
        })
    }

    const pickImage = async (updateType, replaceImg, file) => {
            if(!file){
                return
            }
            if(updateType === 'avatar'){
                updateAccountAvatar(file);
            } 
            if(updateType === 'mastodon_photos'){
                if(replaceImg){
                    console.log('pick img to replace')
                    // pick img to replace
                    setGlobalReplaceImg(replaceImg);
                    // replace img
                    handleUploadPhoto(file);
                    // delete old img
                    handleDeletePhoto(replaceImg);
                } else {
                    console.log('pick img to upload')
                    handleUploadPhoto(file);
                }
            }
    };

    const [first_row_photos, setFirstRow ] = useState([])
    const [second_row_photos, setSecondRow ] = useState([])

    useEffect(() => {
        if(photo_list_value.length < 3){
            let myArray = new Array(2 - photo_list_value.length); 
            myArray.fill("default string"); 
            first_row_photos.fill("default string");
            setFirstRow(myArray);

            let myArray2 = new Array(3); 
            myArray2.fill("default string"); 
            second_row_photos.fill("default string");
            setSecondRow(myArray2)
        }

        if(photo_list_value.length < 6 && photo_list_value.length > 2){
            setFirstRow([])
            let myArray = new Array(5 - photo_list_value.length); 
            myArray.fill("default string"); 
            second_row_photos.fill("default string");
            setSecondRow(myArray);
        }

    }, [photo_list_value])

    useEffect(() => {
        // Force re-render when avatar updates
    }, [avatar])

    if(loadingContainer){
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#732982' }} />
            </Box>
        )
    }

    return (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Profile Photos
            </Typography>
            <Typography variant="body2" color={totalImageCount >= 3 ? "text.secondary" : "error"}>
                Add at least 3 images total (avatar + gallery). {totalImageCount}/3 selected.
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                <Grid item xs={2} sm={4} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, height: '100%', borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, alignSelf: 'flex-start', fontWeight: 700 }}>Main Photo</Typography>
                        <Box
                            component="label"
                            sx={{
                                position: 'relative',
                                width: '65%',
                                maxWidth: 200,
                                aspectRatio: '1 / 1',
                                display: 'block',
                                cursor: 'pointer',
                                '&:hover .avatar-hover-overlay': { opacity: 1 },
                            }}
                        >
                            <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', p: '4px', background: PRIDE_GRADIENT }}>
                                <Box
                                    component="img"
                                    src={avatar}
                                    alt="Avatar"
                                    sx={{ width: '100%', height: '100%', borderRadius: '50%', border: '4px solid #fff', objectFit: 'cover', display: 'block' }}
                                />
                            </Box>
                            <Box className="avatar-hover-overlay" sx={{
                                position: 'absolute', top: '4%', left: '4%', right: '4%', bottom: '4%', borderRadius: '50%',
                                background: 'rgba(0,0,0,0.45)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                opacity: 0, transition: 'opacity 0.15s ease', pointerEvents: 'none',
                            }}>
                                <PhotoCamera sx={{ color: '#fff', fontSize: 26 }} />
                            </Box>
                            <Box sx={{
                                position: 'absolute', bottom: '2%', right: '2%',
                                width: 34, height: 34, borderRadius: '50%',
                                background: '#732982', border: '3px solid #fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                            }}>
                                <PhotoCamera sx={{ color: '#fff', fontSize: 16 }} />
                            </Box>
                            <VisuallyHiddenInput type="file" accept="image/*" onChange={(value) => updateAccountAvatar(value.target.files[0])} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                            Click to update your main photo
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={2} sm={4} md={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
                        {photo_list_value.slice(0, 2).map((item, i) =>
                            <PhotoTile key={i} item={item} onEdit={() => pickImage('mastodon_photos', item)} onDelete={() => handleDeletePhoto(item)} />
                        )}
                        {first_row_photos.map((item, i) =>
                            <UploadTile
                                key={i}
                                onSelect={(file) => pickImage('mastodon_photos', null, file)}
                                warning={i === 0 && validPhotoCount < minimumAdditionalPhotos && (
                                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                                        Add at least {minimumAdditionalPhotos} gallery image{minimumAdditionalPhotos > 1 ? 's' : ''}.
                                    </Typography>
                                )}
                            />
                        )}
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 0.5 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                {photo_list_value.slice(2, 6).map((item, i) =>
                <Grid item xs={2} sm={2} md={4} key={i}>
                    <PhotoTile item={item} onEdit={() => pickImage('mastodon_photos', item)} onDelete={() => handleDeletePhoto(item)} />
                </Grid>
                )}
                {second_row_photos.map((item, i) =>
                <Grid item xs={2} sm={2} md={4} key={i}>
                    <UploadTile onSelect={(file) => pickImage('mastodon_photos', null, file)} />
                </Grid>
                )}
        </Grid>
        </>
    )
}